import React from 'react';
import currency from 'currency.js';
import discountedPrice, { grantsFreeAnnouncements } from '../../../../utils/offerPricing';

// Announcements are identified by name -- the SAME rule the server uses to pick
// the free bundle (Cart#free_announcement_bundle_price), so "Free" here matches
// $0 at checkout.
export const isAnnouncement = (addon) => /announcement/i.test(addon?.name || '');

/**
 * What a customer is ACTUALLY charged for one of these, given the live offer.
 *
 * ⚠️ THE ONE PLACE THAT DECIDES A DISPLAYED PRICE. The component below renders
 * it and the phone buy bar quotes it, so the label and the button a customer
 * presses cannot disagree. They did: StickyBuyBar was handed the raw
 * `addonUnitPrice`, so under any percent offer the panel read "$31.96 $39.95"
 * while the button 160px beneath it read "$39.95" -- and on a phone the button
 * is the only one of the two in view.
 *
 * Returns the KIND as well as the number, so the renderer switches on this
 * rather than re-deriving the same three cases from the same inputs. Two copies
 * of that reasoning is exactly how the two got out of step.
 */
export const priceFor = (price, {
  addon, offer, qualifiesForFreeAnnouncements = false, grantedCount = 0,
} = {}) => {
  // An OFFER put this add-on in the cart (a granted sheet, #5747): the copy
  // they have is free. Decided by the sheet's marker, never by the offer being
  // live -- a decliner, or a second copy they add themselves, pays. Callers
  // quoting the price of ANOTHER copy (the buy bar) must not pass this.
  if (grantedCount > 0) return { kind: 'granted', value: 0, listPrice: price };

  // One announcement bundle is free under a free-announcements offer, but ONLY
  // when the cart actually unlocks the perk (a senior portraits package > $1).
  if (offer && grantsFreeAnnouncements(offer) && qualifiesForFreeAnnouncements && isAnnouncement(addon)) {
    return { kind: 'free', value: 0, listPrice: price };
  }

  const discounted = discountedPrice(price, offer);
  if (discounted != null) return { kind: 'discounted', value: discounted, listPrice: price };

  return { kind: 'plain', value: price, listPrice: price };
};

/**
 * The price line under a product, on both the shop grid and the product screen.
 *
 * Its own file rather than living in ItemAdder: the shop grid (GiftShop) and the
 * product builder (ItemBuilder) both draw it, and ItemAdder imports GiftShop —
 * so keeping it there made the two files import each other.
 */
export default function ItemSubtitle({
  price, addon, offer, qualifiesForFreeAnnouncements = false, showOfferNote = true, grantedCount = 0,
}) {
  // The three cases come from `priceFor` above -- see the note there on why this
  // must not re-derive them. Server gates the free bundle via
  // Cart#qualifies_for_free_announcements?, so "Free" here matches $0 at
  // checkout exactly.
  const { kind, value } = priceFor(price, {
    addon, offer, qualifiesForFreeAnnouncements, grantedCount,
  });

  if (kind === 'granted') {
    // The note stays on even on the shelf (where the percentage note is off):
    // the banner above the grid says a free item is included, this says WHICH
    // one. The product screen adds that extra copies are paid, because its
    // stepper is right underneath.
    return (
      <div>
        <span className="text-gray-500 line-through decoration-2">{currency(price).format()}</span>{' '}
        <span className="font-extrabold text-green-700">Free</span>
        <div className="text-xs font-semibold" style={{ color: '#008fcf' }}>
          {showOfferNote
            ? `${grantedCount} included free with your package — extra copies ${currency(price).format()} each`
            : 'Included with your package'}
        </div>
      </div>
    );
  }

  if (kind === 'free') {
    return (
      <div>
        <span className="text-gray-500 line-through decoration-2">{currency(price).format()}</span>{' '}
        <span className="font-extrabold text-green-700">Free</span>
      </div>
    );
  }

  if (kind === 'discounted') {
    // Attribution line under the strike-through so a customer knows WHY the
    // price dropped. Reads offer.value (the percentage) so any live percentage
    // coupon (GRAD27, CLASSOF27, ...) gets a truthful label without a hardcode.
    //
    // ⚠️ `showOfferNote` IS OFF ON THE SHELF (2026-09-28). The gifts page
    // already carries the offer across a full-width banner directly above the
    // grid, so repeating "20% off" under every tile said the same thing
    // eighteen times. The STRUCK-THROUGH PRICE still shows there -- the
    // customer still sees that it is discounted, they are just not told the
    // percentage once per product.
    //
    // It stays ON everywhere else, and the product screen is the reason: a
    // customer deep in one product has scrolled that banner away.
    return (
      <div>
        {/* NO WEIGHT AND NO COLOUR OF ITS OWN -- both inherit from whatever
            draws it, the same way the plain case below does. The shelf asks for
            regular grey and the product screen for extrabold ink; hardcoding
            700 here kept the tile's discounted prices bold when the shelf went
            regular, and `text-gray-900` kept them near-black when it went grey.
            The same bug twice, so the styling now lives entirely on the caller. */}
        <span>{currency(value).format()}</span>{' '}
        <span className="text-gray-500 line-through decoration-2">{currency(price).format()}</span>
        {showOfferNote && Number(offer?.value) > 0 && (
          <div className="text-xs font-semibold" style={{ color: '#008fcf' }}>
            {`${Number(offer.value)}% off`}
          </div>
        )}
      </div>
    );
  }

  return <div>{currency(price).format()}</div>;
}
